import React from 'react';
import {
  Alert,
  AlertTitle,
  Button,
  Box,
  Typography,
  Paper,
  Stack,
} from '@mui/material';
import { Refresh, Warning, Error as ErrorIcon, Info } from '@mui/icons-material';
import { getErrorInfo, ErrorInfo } from '../../../utils/errorUtils';

interface ErrorDisplayProps {
  error: any;
  onRetry?: () => void;
  variant?: 'alert' | 'card';
  fullWidth?: boolean;
  className?: string;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  onRetry,
  variant = 'alert',
  fullWidth = false,
  className,
}) => {
  const errorInfo: ErrorInfo = getErrorInfo(error);

  const getIcon = () => {
    switch (errorInfo.severity) {
      case 'error':
        return <ErrorIcon />;
      case 'warning':
        return <Warning />;
      case 'info':
        return <Info />;
      default:
        return <ErrorIcon />;
    }
  };

  const commonContent = (
    <>
      <AlertTitle sx={{ fontSize: '1rem', fontWeight: 600 }}>
        {errorInfo.title}
      </AlertTitle>
      <Typography variant="body2" sx={{ mb: errorInfo.showRetryButton ? 2 : 0 }}>
        {errorInfo.message}
      </Typography>
      {errorInfo.showRetryButton && onRetry && (
        <Button
          onClick={onRetry}
          startIcon={<Refresh />}
          variant="outlined"
          size="small"
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 1,
            px: 2,
            py: 0.5,
            fontSize: '0.875rem',
            alignSelf: 'flex-start',
          }}
        >
          Try Again
        </Button>
      )}
    </>
  );

  if (variant === 'card') {
    return (
      <Paper
        elevation={0}
        className={className}
        sx={{
          p: 3,
          textAlign: 'center',
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.02)'
              : 'rgba(0, 0, 0, 0.02)',
          border: (theme) =>
            theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.1)',
          borderRadius: 2,
          width: fullWidth ? '100%' : 'auto',
        }}
      >
        <Stack spacing={2} alignItems="center">
          <Box
            sx={{
              p: 2,
              borderRadius: '50%',
              backgroundColor: (theme) =>
                errorInfo.severity === 'error'
                  ? 'rgba(244, 67, 54, 0.1)'
                  : errorInfo.severity === 'warning'
                  ? 'rgba(255, 152, 0, 0.1)'
                  : 'rgba(33, 150, 243, 0.1)',
              color: (theme) =>
                errorInfo.severity === 'error'
                  ? theme.palette.error.main
                  : errorInfo.severity === 'warning'
                  ? theme.palette.warning.main
                  : theme.palette.info.main,
            }}
          >
            {getIcon()}
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {errorInfo.title}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: errorInfo.showRetryButton ? 2 : 0 }}
            >
              {errorInfo.message}
            </Typography>
            {errorInfo.showRetryButton && onRetry && (
              <Button
                onClick={onRetry}
                startIcon={<Refresh />}
                variant="contained"
                size="small"
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: 1,
                  px: 3,
                  py: 1,
                }}
              >
                Try Again
              </Button>
            )}
          </Box>
        </Stack>
      </Paper>
    );
  }

  return (
    <Alert
      severity={errorInfo.severity}
      icon={getIcon()}
      className={className}
      sx={{
        borderRadius: 1,
        '& .MuiAlert-message': {
          fontSize: '0.9375rem',
          width: '100%',
        },
        ...(fullWidth && { width: '100%' }),
      }}
    >
      {commonContent}
    </Alert>
  );
};

export default ErrorDisplay;